/**
 * useRelationshipOverlay
 *
 * Renders SVG lines on the canvas connecting the currently selected element(s)
 * to every other element they relate to (outgoing + incoming). Lines are
 * colored deterministically per `relationKey` so users can tell different
 * relation types apart at a glance.
 *
 * The overlay lives inside paper's `.joint-viewport`, so zoom/pan transforms
 * are applied automatically and we never have to convert coordinates manually.
 * Crucially, lines are NOT graph cells — they are SVG nodes appended to a
 * dedicated `<g>` group. This keeps `graph.toJSON()` clean and prevents the
 * overlay from being persisted with the map.
 *
 * Lifecycle:
 *   - any time paper / graph / selectedIds / relationTypes change, attempt to
 *     locate (or create) the overlay group, clear it, and re-render lines.
 *   - on unmount the overlay group is detached from the DOM.
 *
 * The "create + render in the same effect" shape is deliberate. JointJS's
 * `.joint-viewport` element does not exist on the very first commit (paper
 * sets it up internally during initial layout), so a separate one-time setup
 * effect keyed on `[paper]` would silently miss it. Doing the lookup on every
 * render makes the hook self-healing.
 */

import { useEffect, useRef } from 'react'
import { dia } from '@joint/core'
import {
  getOrCreateViewportLayer,
  clearLayer,
  removeLayer,
} from '../lib/paperViewportLayer'
import { collectRelationshipLines } from '../lib/relationshipCollect'
import { colorForRelationKey } from '../lib/relationshipColor'
import { computeRelationStrokeWidth } from '../lib/parkingScaleMetrics'
import type { TemplateRelationType } from '../../../map-editor-entities/schema/templateSchema'

const SVG_NS = 'http://www.w3.org/2000/svg'
const OVERLAY_GROUP_ID = 'relationship-overlay'

/**
 * The previous `useRelationshipVisualization` hook drew its lines as JointJS
 * link cells (`graph.addCell(link)` with `isRelationshipVisualization: true`).
 * If `useGraphAutoSave` fired between create and clear, those marker links
 * were persisted into `graphJson` and they keep coming back on every reload,
 * appearing as "always visible" lines unrelated to the current selection.
 *
 * This hook is the new owner of relationship visualization, so on every graph
 * load we scrub any leftover debris cells. The scrub mutates the live graph;
 * the next graphAutoSave tick then writes a clean graphJson to the server,
 * eventually purging the persistent state for this floor.
 */
function scrubLegacyRelationshipLinkCells(graph: dia.Graph): void {
  const debris = graph.getLinks().filter(
    (link) => link.get('isRelationshipVisualization') === true,
  )
  if (debris.length === 0) return
  for (const link of debris) link.remove()
}

export function useRelationshipOverlay(
  paper: dia.Paper | null,
  graph: dia.Graph | null,
  selectedIds: ReadonlySet<string>,
  relationTypes: Record<string, TemplateRelationType> | undefined,
): void {
  // Track the overlay group across renders so the unmount cleanup can detach it.
  const groupRef = useRef<SVGGElement | null>(null)

  // Scrub persistent debris from the legacy graph-cell visualization the
  // moment a graph becomes available (and again whenever JointJS reloads it,
  // e.g. via fromJSON on floor switch / dashboard re-entry). This is what
  // kept old red links visible "for everything, all the time" even after
  // selection cleared. Listening to `reset` covers fromJSON; the initial
  // scrub covers an already-loaded graph at mount time.
  useEffect(() => {
    if (!graph) return
    scrubLegacyRelationshipLinkCells(graph)
    const onReset = () => scrubLegacyRelationshipLinkCells(graph)
    graph.on('reset', onReset)
    return () => {
      graph.off('reset', onReset)
    }
  }, [graph])

  useEffect(() => {
    if (!paper) return

    // Create-or-reuse the overlay layer on every effect run so we recover
    // from the case where `.joint-viewport` was not yet present on first mount.
    const group = getOrCreateViewportLayer(paper, OVERLAY_GROUP_ID)
    if (!group) return
    groupRef.current = group

    clearLayer(group)

    if (!graph) return
    const lines = collectRelationshipLines(selectedIds, graph, relationTypes)
    if (lines.length === 0) return

    // 주차구역 외곽선과 비슷한 굵기로 — 그래프 스케일이 달라도(신규 렌더 vs
    // 재진입) 주차면 strokeWidth 를 따라가므로 항상 비례가 맞는다.
    const strokeWidth = computeRelationStrokeWidth(graph)
    // dash 패턴도 굵기에 비례시켜 줌 레벨과 무관하게 동일한 점선 느낌 유지.
    const dashPattern = `${strokeWidth * 3} ${strokeWidth * 1.5}`

    for (const { sourceId, targetId, relationKey, bidirectional } of lines) {
      const src = graph.getCell(sourceId)
      const dst = graph.getCell(targetId)
      if (!src || !dst) continue
      // Both must be elements (links don't have a bbox usable as an anchor).
      if (typeof (src as dia.Element).getBBox !== 'function') continue
      if (typeof (dst as dia.Element).getBBox !== 'function') continue

      const sb = (src as dia.Element).getBBox()
      const db = (dst as dia.Element).getBBox()
      if (!sb || !db) continue

      const x1 = sb.x + sb.width / 2
      const y1 = sb.y + sb.height / 2
      const x2 = db.x + db.width / 2
      const y2 = db.y + db.height / 2

      const line = document.createElementNS(SVG_NS, 'line')
      line.setAttribute('x1', String(x1))
      line.setAttribute('y1', String(y1))
      line.setAttribute('x2', String(x2))
      line.setAttribute('y2', String(y2))
      line.setAttribute('stroke', colorForRelationKey(relationKey))
      // 주차구역 외곽선 굵기에 맞춘 stroke (computeRelationStrokeWidth).
      // Bidirectional links use a dashed pattern so they read distinctly
      // from one-way relations even at a glance.
      line.setAttribute('stroke-width', String(strokeWidth))
      line.setAttribute('stroke-opacity', '0.85')
      line.setAttribute('stroke-linecap', 'round')
      if (bidirectional) {
        line.setAttribute('stroke-dasharray', dashPattern)
      }
      line.setAttribute('pointer-events', 'none')
      line.dataset.relationKey = relationKey
      line.dataset.sourceId = sourceId
      line.dataset.targetId = targetId
      if (bidirectional) line.dataset.bidirectional = 'true'
      group.appendChild(line)
    }
  }, [paper, graph, selectedIds, relationTypes])

  // Final unmount cleanup — detach the overlay group from the DOM.
  useEffect(() => {
    return () => {
      const g = groupRef.current
      if (g) {
        removeLayer(g)
        groupRef.current = null
      }
    }
  }, [])
}
